import type { OrderStatus } from './types';

export interface StatusMeta {
  label: string;
  short: string;
  step: number; // position in the 7-step happy path; 0 for exceptions
  icon: string; // lucide icon name (kebab-case)
  badge: string; // tailwind classes for the pill
  dot: string; // tailwind bg class for dots / progress segments
  hex: string; // for map markers and inline styles
}

export const STATUS: Record<OrderStatus, StatusMeta> = {
  AWAITING_ADDRESS: { label: 'Awaiting Drop-off Pin', short: 'Awaiting pin', step: 0, icon: 'map-pin', hex: '#a855f7', dot: 'bg-purple-500',
    badge: 'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-500/15 dark:text-purple-300' },
  DRAFT: { label: 'Scheduled', short: 'Scheduled', step: 1, icon: 'calendar-clock', hex: '#94a3b8', dot: 'bg-slate-400',
    badge: 'bg-slate-100 text-slate-700 ring-slate-500/20 dark:bg-slate-500/15 dark:text-slate-300' },
  SEARCHING_RIDER: { label: 'Searching Rider', short: 'Searching', step: 2, icon: 'radar', hex: '#f59e0b', dot: 'bg-amber-500',
    badge: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-500/15 dark:text-amber-300' },
  RIDER_ACCEPTED: { label: 'Rider En Route to Pickup', short: 'To Pickup', step: 3, icon: 'bike', hex: '#0ea5e9', dot: 'bg-sky-500',
    badge: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-500/15 dark:text-sky-300' },
  ARRIVED_AT_PICKUP: { label: 'Arrived at Pickup', short: 'At Pickup', step: 4, icon: 'store', hex: '#8b5cf6', dot: 'bg-violet-500',
    badge: 'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-500/15 dark:text-violet-300' },
  PICKED_UP: { label: 'Package Picked Up', short: 'Picked Up', step: 5, icon: 'package-check', hex: '#6366f1', dot: 'bg-indigo-500',
    badge: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20 dark:bg-indigo-500/15 dark:text-indigo-300' },
  IN_TRANSIT: { label: 'Out for Delivery', short: 'In Transit', step: 6, icon: 'truck', hex: '#06b6d4', dot: 'bg-cyan-500',
    badge: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20 dark:bg-cyan-500/15 dark:text-cyan-300' },
  DELIVERED: { label: 'Delivered', short: 'Delivered', step: 7, icon: 'circle-check-big', hex: '#10b981', dot: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/15 dark:text-emerald-300' },
  CANCELLED: { label: 'Cancelled', short: 'Cancelled', step: 0, icon: 'circle-x', hex: '#f43f5e', dot: 'bg-rose-500',
    badge: 'bg-rose-50 text-rose-700 ring-rose-600/20 dark:bg-rose-500/15 dark:text-rose-300' },
  FAILED_DELIVERY: { label: 'Failed Delivery', short: 'Failed', step: 0, icon: 'triangle-alert', hex: '#f97316', dot: 'bg-orange-500',
    badge: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-500/15 dark:text-orange-300' },
  RETURNED: { label: 'Returned to Merchant', short: 'Returned', step: 0, icon: 'undo-2', hex: '#78716c', dot: 'bg-stone-500',
    badge: 'bg-stone-100 text-stone-700 ring-stone-500/20 dark:bg-stone-500/15 dark:text-stone-300' },
};

/** The seven happy-path states, in order. */
export const FLOW: OrderStatus[] = ['DRAFT', 'SEARCHING_RIDER', 'RIDER_ACCEPTED', 'ARRIVED_AT_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'DELIVERED'];

export const RIDER_BUSY: OrderStatus[] = ['RIDER_ACCEPTED', 'ARRIVED_AT_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'FAILED_DELIVERY'];
/** A rider is on the way, so the recipient's delivery code is live and can be re-sent. */
export const CODE_LIVE: OrderStatus[] = ['RIDER_ACCEPTED', 'ARRIVED_AT_PICKUP', 'PICKED_UP', 'IN_TRANSIT'];
export const ACTIVE: OrderStatus[] = ['SEARCHING_RIDER', ...RIDER_BUSY];
export const TERMINAL: OrderStatus[] = ['DELIVERED', 'CANCELLED', 'RETURNED'];

/** Cancellation is allowed only before the package is picked up. */
export const canCancel = (s: OrderStatus): boolean => ['AWAITING_ADDRESS', 'DRAFT', 'SEARCHING_RIDER', 'RIDER_ACCEPTED', 'ARRIVED_AT_PICKUP'].includes(s);

export const isException = (s: OrderStatus): boolean => ['CANCELLED', 'FAILED_DELIVERY', 'RETURNED'].includes(s);
